import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import EmailIcon from '@mui/icons-material/Mail';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import PrintIcon from '@mui/icons-material/Print';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import dayjs from 'dayjs';
import PageHeader from '../components/PageHeader';
import LoadingFallback from '../components/LoadingFallback';
import EmptyState from '../components/EmptyState';
import StatusChip from '../components/StatusChip';
import FormSection from '../components/FormSection';
import { api, apiErrorMessage } from '../api/client';
import { useFeedback } from '../contexts/FeedbackContext';
import { useAuth } from '../contexts/AuthContext';
import type {
  Division,
  EventType,
  Meet,
  MeetEvent,
  MeetStatus,
  Registration,
  RegistrationStatus,
  ScheduleEntry,
  Student,
  Venue,
} from '../api/types';

const DIVISIONS: { value: Division; label: string }[] = [
  { value: 'open', label: 'Open' },
  { value: 'boys', label: 'Boys' },
  { value: 'girls', label: 'Girls' },
  { value: 'mixed', label: 'Mixed' },
];

const STATUSES: { value: MeetStatus; label: string }[] = [
  { value: 'draft', label: 'Draft' },
  { value: 'published', label: 'Published' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'complete', label: 'Complete' },
  { value: 'cancelled', label: 'Cancelled' },
];

const REG_STATUSES: { value: RegistrationStatus; label: string }[] = [
  { value: 'registered', label: 'Registered' },
  { value: 'scratched', label: 'Scratched' },
  { value: 'dns', label: 'DNS' },
  { value: 'dnf', label: 'DNF' },
  { value: 'dq', label: 'DQ' },
];

export default function MeetDetailPage() {
  const { id } = useParams();
  const meetId = Number(id);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { confirm, toast } = useFeedback();
  const { user } = useAuth();
  const [tab, setTab] = useState(0);
  const [editOpen, setEditOpen] = useState(false);
  const [addEventOpen, setAddEventOpen] = useState(false);
  const [editEvent, setEditEvent] = useState<MeetEvent | null>(null);
  const [registerEvent, setRegisterEvent] = useState<MeetEvent | null>(null);

  const meetQuery = useQuery<Meet>({
    queryKey: ['meets', meetId],
    queryFn: async () => (await api.get(`/meets/${meetId}`)).data,
  });
  const eventsQuery = useQuery<MeetEvent[]>({
    queryKey: ['meets', meetId, 'events'],
    queryFn: async () => (await api.get(`/meet-events/by-meet/${meetId}`)).data,
  });
  const venuesQuery = useQuery<Venue[]>({
    queryKey: ['venues'],
    queryFn: async () => (await api.get('/venues')).data,
  });
  const eventTypesQuery = useQuery<EventType[]>({
    queryKey: ['event-types'],
    queryFn: async () => (await api.get('/event-types')).data,
  });
  const scheduleQuery = useQuery<{ entries: ScheduleEntry[] }>({
    queryKey: ['schedule', 'meet', meetId],
    queryFn: async () => (await api.get(`/schedule/meet/${meetId}`)).data,
  });

  const updateMeet = useMutation({
    mutationFn: async (payload: any) => (await api.patch(`/meets/${meetId}`, payload)).data,
    onSuccess: () => {
      toast({ message: 'Meet updated', severity: 'success' });
      setEditOpen(false);
      qc.invalidateQueries({ queryKey: ['meets'] });
    },
    onError: (e) => toast({ message: apiErrorMessage(e), severity: 'error' }),
  });

  const deleteMeet = useMutation({
    mutationFn: async () => (await api.delete(`/meets/${meetId}`)).data,
    onSuccess: () => {
      toast({ message: 'Meet deleted', severity: 'info' });
      navigate('/meets');
    },
    onError: (e) => toast({ message: apiErrorMessage(e), severity: 'error' }),
  });

  const addEvent = useMutation({
    mutationFn: async (payload: any) => (await api.post(`/meet-events/by-meet/${meetId}`, payload)).data,
    onSuccess: () => {
      toast({ message: 'Event added', severity: 'success' });
      setAddEventOpen(false);
      qc.invalidateQueries({ queryKey: ['meets', meetId] });
    },
    onError: (e) => toast({ message: apiErrorMessage(e), severity: 'error' }),
  });

  const updateEvent = useMutation({
    mutationFn: async ({ id: eid, payload }: { id: number; payload: any }) =>
      (await api.patch(`/meet-events/${eid}`, payload)).data,
    onSuccess: () => {
      toast({ message: 'Event updated', severity: 'success' });
      setEditEvent(null);
      qc.invalidateQueries({ queryKey: ['meets', meetId] });
    },
    onError: (e) => toast({ message: apiErrorMessage(e), severity: 'error' }),
  });

  const deleteEvent = useMutation({
    mutationFn: async (eid: number) => (await api.delete(`/meet-events/${eid}`)).data,
    onSuccess: () => {
      toast({ message: 'Event removed', severity: 'info' });
      qc.invalidateQueries({ queryKey: ['meets', meetId] });
    },
    onError: (e) => toast({ message: apiErrorMessage(e), severity: 'error' }),
  });

  if (meetQuery.isLoading || !meetQuery.data) return <LoadingFallback />;
  const meet = meetQuery.data;

  return (
    <Box>
      <PageHeader
        title={meet.name}
        subtitle={[
          dayjs(meet.start_date).format('MMM D, YYYY'),
          meet.end_date !== meet.start_date ? dayjs(meet.end_date).format('MMM D') : null,
          meet.venue?.name,
        ].filter(Boolean).join(' · ')}
        actions={
          <Stack direction="row" spacing={1.5} alignItems="center">
            <StatusChip status={meet.status} />
            <Button variant="contained" startIcon={<AssignmentTurnedInIcon />} onClick={() => navigate(`/meets/${meet.id}/prepare`)} aria-label="Prepare meet">
              Prepare
            </Button>
            <Button variant="outlined" startIcon={<PrintIcon />} onClick={() => navigate(`/meets/${meet.id}/coach-pack`)} aria-label="Coach pack">
              Coach Pack
            </Button>
            <Button variant="outlined" startIcon={<PlayCircleIcon />} onClick={() => navigate(`/meets/${meet.id}/day`)} aria-label="Meet day mode">
              Meet Day
            </Button>
            <Button variant="outlined" startIcon={<EmailIcon />} onClick={() => navigate(`/notifications?meet=${meet.id}`)} aria-label="Email parents">
              Email parents
            </Button>
            <Button variant="outlined" startIcon={<EditIcon />} onClick={() => setEditOpen(true)} aria-label="Edit meet">
              Edit
            </Button>
            {user?.role === 'admin' && (
              <Button
                color="error"
                variant="outlined"
                startIcon={<DeleteIcon />}
                onClick={async () => {
                  const ok = await confirm({
                    title: 'Delete meet?',
                    message: `Delete ${meet.name} and all events/registrations?`,
                    destructive: true,
                  });
                  if (ok) deleteMeet.mutate();
                }}
                aria-label="Delete meet"
              >
                Delete
              </Button>
            )}
          </Stack>
        }
      />

      {meet.description && (
        <Alert severity="info" sx={{ mb: 2 }}>{meet.description}</Alert>
      )}

      <Card>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
          <Tab label="Events" id="tab-events" aria-controls="panel-events" />
          <Tab label="Program" id="tab-program" aria-controls="panel-program" />
          <Tab label="Details" id="tab-details" aria-controls="panel-details" />
        </Tabs>

        {tab === 0 && (
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="subtitle1">
                {(eventsQuery.data ?? []).length} event{(eventsQuery.data ?? []).length === 1 ? '' : 's'}
              </Typography>
              <Button startIcon={<AddIcon />} variant="contained" onClick={() => setAddEventOpen(true)} aria-label="Add event">
                Add event
              </Button>
            </Stack>
            {eventsQuery.isLoading ? (
              <LoadingFallback />
            ) : (eventsQuery.data ?? []).length === 0 ? (
              <EmptyState title="No events yet" description="Add the events you'll be running at this meet." />
            ) : (
              <Stack spacing={1.5}>
                {(eventsQuery.data ?? []).map((ev) => (
                  <EventRow
                    key={ev.id}
                    event={ev}
                    onEdit={() => setEditEvent(ev)}
                    onDelete={async () => {
                      const ok = await confirm({
                        title: 'Remove event?',
                        message: `Remove ${ev.event_type.name} (${ev.division}) from this meet?`,
                        destructive: true,
                      });
                      if (ok) deleteEvent.mutate(ev.id);
                    }}
                    onRegister={() => setRegisterEvent(ev)}
                  />
                ))}
              </Stack>
            )}
          </CardContent>
        )}

        {tab === 1 && (
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>Program</Typography>
            {scheduleQuery.isLoading ? (
              <LoadingFallback />
            ) : (scheduleQuery.data?.entries ?? []).length === 0 ? (
              <EmptyState title="No registrations yet" description="Add events and register athletes to populate the program." />
            ) : (
              <Table size="small" aria-label="Meet program">
                <TableHead>
                  <TableRow>
                    <TableCell>When</TableCell>
                    <TableCell>Event</TableCell>
                    <TableCell>Division</TableCell>
                    <TableCell>Athlete</TableCell>
                    <TableCell>Heat / Lane</TableCell>
                    <TableCell>Area</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(scheduleQuery.data?.entries ?? []).map((e) => (
                    <TableRow key={e.registration_id}>
                      <TableCell>{e.scheduled_start_at ? dayjs(e.scheduled_start_at).format('MMM D, h:mm A') : 'TBD'}</TableCell>
                      <TableCell>{e.event_name}</TableCell>
                      <TableCell>{e.division}{e.age_group ? ` · ${e.age_group}` : ''}</TableCell>
                      <TableCell>{e.student_name}</TableCell>
                      <TableCell>{e.heat_number ? `H${e.heat_number}${e.lane ? ` · L${e.lane}` : ''}` : '—'}</TableCell>
                      <TableCell>{e.area || '—'}</TableCell>
                      <TableCell><StatusChip status={e.status} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        )}

        {tab === 2 && (
          <CardContent>
            <Typography variant="h6" sx={{ mb: 1 }}>Details</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">Status</Typography>
                <Box><StatusChip status={meet.status} /></Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">Venue</Typography>
                <Typography>{meet.venue?.name || '—'}</Typography>
                {meet.venue?.address && (
                  <Typography variant="caption" color="text.secondary">{meet.venue.address}</Typography>
                )}
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">Check-in instructions</Typography>
                <Typography sx={{ whiteSpace: 'pre-wrap' }}>
                  {meet.check_in_instructions || '— None —'}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        )}
      </Card>

      <EditMeetDialog
        open={editOpen}
        meet={meet}
        venues={venuesQuery.data ?? []}
        onClose={() => setEditOpen(false)}
        onSubmit={(p) => updateMeet.mutate(p)}
        busy={updateMeet.isPending}
      />

      <AddEventDialog
        open={addEventOpen}
        onClose={() => setAddEventOpen(false)}
        eventTypes={eventTypesQuery.data ?? []}
        onSubmit={(p) => addEvent.mutate(p)}
        busy={addEvent.isPending}
      />

      <EditEventDialog
        event={editEvent}
        onClose={() => setEditEvent(null)}
        onSubmit={(p) => editEvent && updateEvent.mutate({ id: editEvent.id, payload: p })}
        busy={updateEvent.isPending}
      />

      <RegistrationsDialog
        meetEvent={registerEvent}
        onClose={() => setRegisterEvent(null)}
      />
    </Box>
  );
}

function EventRow({
  event,
  onEdit,
  onDelete,
  onRegister,
}: {
  event: MeetEvent;
  onEdit: () => void;
  onDelete: () => void;
  onRegister: () => void;
}) {
  return (
    <Box
      sx={{
        p: 2,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
      }}
    >
      <Box sx={{ flex: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{event.event_type.name}</Typography>
          <Chip size="small" label={event.division} />
          {event.age_group && <Chip size="small" label={event.age_group} variant="outlined" />}
          <Chip size="small" label={event.event_type.code} variant="outlined" />
        </Stack>
        <Typography variant="caption" color="text.secondary">
          {event.scheduled_start_at ? dayjs(event.scheduled_start_at).format('ddd MMM D · h:mm A') : 'Time TBD'}
          {event.area ? ` · ${event.area}` : ''}
          {` · ${event.registration_count ?? 0} entries`}
        </Typography>
      </Box>
      <Stack direction="row" spacing={0.5}>
        <Button onClick={onRegister} aria-label={`Register athletes in ${event.event_type.name}`}>Register</Button>
        <IconButton aria-label={`Edit ${event.event_type.name}`} onClick={onEdit}><EditIcon fontSize="small" /></IconButton>
        <IconButton aria-label={`Remove ${event.event_type.name}`} onClick={onDelete}><DeleteIcon fontSize="small" /></IconButton>
      </Stack>
    </Box>
  );
}

function EditMeetDialog({
  open,
  meet,
  venues,
  onClose,
  onSubmit,
  busy,
}: {
  open: boolean;
  meet: Meet;
  venues: Venue[];
  onClose: () => void;
  onSubmit: (p: any) => void;
  busy: boolean;
}) {
  const [name, setName] = useState(meet.name);
  const [start, setStart] = useState(meet.start_date);
  const [end, setEnd] = useState(meet.end_date);
  const [venueId, setVenueId] = useState<string>(meet.venue_id ? String(meet.venue_id) : '');
  const [description, setDescription] = useState(meet.description || '');
  const [checkIn, setCheckIn] = useState(meet.check_in_instructions || '');
  const [status, setStatus] = useState<MeetStatus>(meet.status);

  useEffect(() => {
    if (open) {
      setName(meet.name);
      setStart(meet.start_date);
      setEnd(meet.end_date);
      setVenueId(meet.venue_id ? String(meet.venue_id) : '');
      setDescription(meet.description || '');
      setCheckIn(meet.check_in_instructions || '');
      setStatus(meet.status);
    }
  }, [open, meet]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Edit meet</DialogTitle>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit({
            name,
            start_date: start,
            end_date: end,
            venue_id: venueId ? Number(venueId) : null,
            description,
            check_in_instructions: checkIn,
            status,
          });
        }}
        aria-label="Edit meet form"
      >
        <DialogContent>
          <FormSection title="Meet info">
            <Stack spacing={2}>
              <TextField label="Name" value={name} onChange={(e) => setName(e.target.value)} required />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Start date"
                    type="date"
                    value={start}
                    onChange={(e) => setStart(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="End date"
                    type="date"
                    value={end}
                    onChange={(e) => setEnd(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    required
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField label="Venue" select value={venueId} onChange={(e) => setVenueId(e.target.value)}>
                    <MenuItem value="">— None —</MenuItem>
                    {venues.map((v) => (
                      <MenuItem key={v.id} value={String(v.id)}>{v.name}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField label="Status" select value={status} onChange={(e) => setStatus(e.target.value as MeetStatus)}>
                    {STATUSES.map((s) => (
                      <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </Grid>
              <TextField label="Description" multiline minRows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
              <TextField
                label="Check-in instructions"
                multiline
                minRows={3}
                value={checkIn}
                onChange={(e) => setCheckIn(e.target.value)}
                helperText="Included in parent emails."
              />
            </Stack>
          </FormSection>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} aria-label="Cancel">Cancel</Button>
          <Button type="submit" variant="contained" disabled={busy} aria-label="Save meet">
            {busy ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

function AddEventDialog({
  open,
  onClose,
  eventTypes,
  onSubmit,
  busy,
}: {
  open: boolean;
  onClose: () => void;
  eventTypes: EventType[];
  onSubmit: (p: any) => void;
  busy: boolean;
}) {
  const [eventType, setEventType] = useState<EventType | null>(null);
  const [division, setDivision] = useState<Division>('open');
  const [ageGroup, setAgeGroup] = useState('');
  const [scheduledAt, setScheduledAt] = useState('');
  const [area, setArea] = useState('');

  useEffect(() => {
    if (open) {
      setEventType(null);
      setDivision('open');
      setAgeGroup('');
      setScheduledAt('');
      setArea('');
    }
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Add event to meet</DialogTitle>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!eventType) return;
          onSubmit({
            event_type_id: eventType.id,
            division,
            age_group: ageGroup || undefined,
            scheduled_start_at: scheduledAt ? new Date(scheduledAt).toISOString() : undefined,
            area: area || undefined,
          });
        }}
        aria-label="Add event form"
      >
        <DialogContent>
          <Stack spacing={2}>
            <Autocomplete
              options={eventTypes}
              groupBy={(o) => o.category}
              getOptionLabel={(o) => `${o.name} (${o.code})`}
              value={eventType}
              onChange={(_, val) => setEventType(val)}
              renderInput={(params) => <TextField {...params} label="Event" required />}
            />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField label="Division" select value={division} onChange={(e) => setDivision(e.target.value as Division)}>
                  {DIVISIONS.map((d) => (
                    <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="Age group" placeholder="e.g. U14" value={ageGroup} onChange={(e) => setAgeGroup(e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Scheduled start"
                  type="datetime-local"
                  value={scheduledAt}
                  onChange={(e) => setScheduledAt(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="Area" placeholder="e.g. Track, Pit 2" value={area} onChange={(e) => setArea(e.target.value)} />
              </Grid>
            </Grid>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} aria-label="Cancel">Cancel</Button>
          <Button type="submit" variant="contained" disabled={busy || !eventType} aria-label="Add event">
            {busy ? 'Adding…' : 'Add event'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

function EditEventDialog({
  event,
  onClose,
  onSubmit,
  busy,
}: {
  event: MeetEvent | null;
  onClose: () => void;
  onSubmit: (p: any) => void;
  busy: boolean;
}) {
  const [division, setDivision] = useState<Division>('open');
  const [ageGroup, setAgeGroup] = useState('');
  const [scheduledAt, setScheduledAt] = useState('');
  const [area, setArea] = useState('');

  useEffect(() => {
    if (event) {
      setDivision(event.division);
      setAgeGroup(event.age_group || '');
      setScheduledAt(event.scheduled_start_at ? dayjs(event.scheduled_start_at).format('YYYY-MM-DDTHH:mm') : '');
      setArea(event.area || '');
    }
  }, [event]);

  return (
    <Dialog
      open={!!event}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>Edit event</DialogTitle>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit({
            division,
            age_group: ageGroup,
            scheduled_start_at: scheduledAt ? new Date(scheduledAt).toISOString() : null,
            area,
          });
        }}
        aria-label="Edit event form"
      >
        <DialogContent>
          <Stack spacing={2}>
            <Typography variant="subtitle1">{event?.event_type.name}</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField label="Division" select value={division} onChange={(e) => setDivision(e.target.value as Division)}>
                  {DIVISIONS.map((d) => (
                    <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="Age group" value={ageGroup} onChange={(e) => setAgeGroup(e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Scheduled start"
                  type="datetime-local"
                  value={scheduledAt}
                  onChange={(e) => setScheduledAt(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="Area" value={area} onChange={(e) => setArea(e.target.value)} />
              </Grid>
            </Grid>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} aria-label="Cancel">Cancel</Button>
          <Button type="submit" variant="contained" disabled={busy} aria-label="Save event">
            {busy ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

function RegistrationsDialog({
  meetEvent,
  onClose,
}: {
  meetEvent: MeetEvent | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { confirm, toast } = useFeedback();
  const eventId = meetEvent?.id;

  const studentsQuery = useQuery<Student[]>({
    queryKey: ['students', 'list-all'],
    enabled: !!meetEvent,
    queryFn: async () => (await api.get('/students')).data,
  });
  const regsQuery = useQuery<Registration[]>({
    queryKey: ['registrations', eventId],
    enabled: !!meetEvent,
    queryFn: async () => (await api.get(`/registrations/by-meet-event/${eventId}`)).data,
  });

  const [picked, setPicked] = useState<Student[]>([]);

  useEffect(() => {
    if (!meetEvent) setPicked([]);
  }, [meetEvent]);

  const addRegs = useMutation({
    mutationFn: async () => {
      if (!eventId || picked.length === 0) return [];
      return (await api.post(`/registrations/by-meet-event/${eventId}`, {
        student_ids: picked.map((s) => s.id),
      })).data;
    },
    onSuccess: () => {
      toast({ message: 'Registered', severity: 'success' });
      setPicked([]);
      qc.invalidateQueries({ queryKey: ['registrations', eventId] });
      qc.invalidateQueries({ queryKey: ['meets', meetEvent?.meet_id] });
      qc.invalidateQueries({ queryKey: ['schedule', 'meet', meetEvent?.meet_id] });
    },
    onError: (e) => toast({ message: apiErrorMessage(e), severity: 'error' }),
  });

  const updateReg = useMutation({
    mutationFn: async ({ id, payload }: { id: number; payload: any }) =>
      (await api.patch(`/registrations/${id}`, payload)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['registrations', eventId] }),
  });

  const removeReg = useMutation({
    mutationFn: async (id: number) => (await api.delete(`/registrations/${id}`)).data,
    onSuccess: () => {
      toast({ message: 'Removed', severity: 'info' });
      qc.invalidateQueries({ queryKey: ['registrations', eventId] });
      qc.invalidateQueries({ queryKey: ['meets', meetEvent?.meet_id] });
    },
  });

  const registeredIds = new Set((regsQuery.data ?? []).map((r) => r.student_id));
  const options = (studentsQuery.data ?? []).filter((s) => !registeredIds.has(s.id));

  return (
    <Dialog open={!!meetEvent} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        Registrations · {meetEvent?.event_type.name} ({meetEvent?.division})
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Add athletes</Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <Autocomplete
                multiple
                sx={{ flex: 1 }}
                options={options}
                getOptionLabel={(o) => `${o.full_name}${o.grade ? ` · ${o.grade}` : ''}`}
                value={picked}
                onChange={(_, val) => setPicked(val)}
                renderInput={(params) => (
                  <TextField {...params} label="Pick athletes" inputProps={{ ...params.inputProps, 'aria-label': 'Pick athletes' }} />
                )}
              />
              <Button
                variant="contained"
                disabled={addRegs.isPending || picked.length === 0}
                onClick={() => addRegs.mutate()}
                aria-label="Register selected athletes"
              >
                Register
              </Button>
            </Stack>
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Currently registered</Typography>
            {regsQuery.isLoading ? (
              <LoadingFallback height={120} />
            ) : (regsQuery.data ?? []).length === 0 ? (
              <EmptyState title="Nobody registered yet" />
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Athlete</TableCell>
                    <TableCell>Seed</TableCell>
                    <TableCell>Lane</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(regsQuery.data ?? []).map((r) => (
                    <TableRow key={r.id}>
                      <TableCell>
                        <Typography variant="body2"><strong>{r.student.full_name}</strong></Typography>
                        {r.student.grade && <Typography variant="caption" color="text.secondary">Grade {r.student.grade}</Typography>}
                      </TableCell>
                      <TableCell sx={{ width: 130 }}>
                        <TextField
                          size="small"
                          value={r.seed_mark || ''}
                          onChange={(e) => updateReg.mutate({ id: r.id, payload: { seed_mark: e.target.value } })}
                          placeholder="—"
                          inputProps={{ 'aria-label': 'Seed mark' }}
                        />
                      </TableCell>
                      <TableCell sx={{ width: 100 }}>
                        <TextField
                          size="small"
                          type="number"
                          value={r.lane ?? ''}
                          onChange={(e) =>
                            updateReg.mutate({
                              id: r.id,
                              payload: { lane: e.target.value ? Number(e.target.value) : null },
                            })
                          }
                          placeholder="—"
                          inputProps={{ 'aria-label': 'Lane', min: 1 }}
                        />
                      </TableCell>
                      <TableCell sx={{ width: 160 }}>
                        <TextField
                          size="small"
                          select
                          value={r.status}
                          onChange={(e) => updateReg.mutate({ id: r.id, payload: { status: e.target.value } })}
                          inputProps={{ 'aria-label': 'Status' }}
                        >
                          {REG_STATUSES.map((s) => (
                            <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
                          ))}
                        </TextField>
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          aria-label={`Remove ${r.student.full_name}`}
                          onClick={async () => {
                            const ok = await confirm({
                              title: 'Remove registration?',
                              message: `Remove ${r.student.full_name} from this event?`,
                              destructive: true,
                            });
                            if (ok) removeReg.mutate(r.id);
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained" aria-label="Close registrations">Done</Button>
      </DialogActions>
    </Dialog>
  );
}
